import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import {
  AuthPasswordResetApiArg,
  AuthTotpApiArg,
  esApi as baseApi,
  GetOrgInvoiceListApiResponse,
  UploadFileApiArg,
} from '@/__generated/es-api'
import {
  addCustomFieldsSuccess,
  deleteCustomFieldsSuccess,
  getCustomFieldsSuccess,
  modCustomFieldsSuccess,
} from '@/app/module/custom-fields/store/actions'
import { errorCodes } from '@/app/service/util/error-codes'

/**
 * Creates an invalidatesTags function that only invalidates tags on successful requests
 * @param tags - Array of tag names to invalidate on success
 * @returns Function that returns empty array on error, or the tags array on success
 */
const createInvalidate =
  <T extends readonly string[]>(tags: T) =>
  (_: unknown, error: FetchBaseQueryError | undefined): T | [] =>
    error ? [] : tags

export const esApi = baseApi.enhanceEndpoints({
  addTagTypes: [
    'Contact',
    'CustomFields',
    'Organization',
    'OrgBalance',
    'OrgCreditCard',
    'OrgCampaign',
    'OrgDid',
    'OrgExport',
    'OrgFiles',
    'OrgGroup',
    'OrgGroups',
    'OrgInvite',
    'OrgPlan',
    'OrgQuota',
    'OrgSenderId',
    'OrgSnippet',
    'OrgUser',
    'WaTemplate',
    'TwoFactor',
    'AuthToken',
  ],
  endpoints: {
    authPasswordReset: {
      query: (args: AuthPasswordResetApiArg) => {
        const data = new URLSearchParams()
        data.append('email', args.body.email)

        return {
          url: `/v1/auth/passwordreset`,
          method: 'POST',
          body: data,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      },
    },
    authTotp: {
      query: (args: AuthTotpApiArg) => {
        const data = new URLSearchParams()
        if (args.body.recovery) {
          data.append('recovery', args.body.recovery)
        }
        if (args.body.passcode) {
          data.append('passcode', args.body.passcode)
        }

        return {
          url: `/v1/auth/totp`,
          method: 'POST',
          body: data,
          headers: {
            Authorization: args.authorization,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      },
    },
    getContact: {
      providesTags: ['Contact'],
    },
    getOrgAutodelete: {
      providesTags: ['Organization'],
    },
    getOrgCustomfields: {
      providesTags: ['CustomFields'],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled
          // TODO: remove this when we switch from using boilerplate to rtk query
          dispatch(getCustomFieldsSuccess(result.data.allCustomfields))
        } catch {
          // do nothing
        }
      },
    },
    createOrgCustomfield: {
      invalidatesTags: createInvalidate(['CustomFields'] as const),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled
          arg.body.names?.forEach((name, index) => {
            dispatch(
              addCustomFieldsSuccess({
                id: result.data.ids[index],
                name,
              }),
            )
          })
        } catch {
          // do nothing
        }
      },
    },
    updateOrgCustomfield: {
      invalidatesTags: createInvalidate(['CustomFields'] as const),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled
          dispatch(modCustomFieldsSuccess(result.data))
        } catch {
          // do nothing
        }
      },
    },
    deleteOrgCustomfield: {
      invalidatesTags: createInvalidate(['CustomFields'] as const),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled
          dispatch(deleteCustomFieldsSuccess({ itemId: Number(arg.customfieldId) }))
        } catch {
          // do nothing
        }
      },
    },
    getOrgDid: {
      providesTags: ['OrgDid'],
    },
    getOrgDids: {
      providesTags: ['OrgDid'],
    },
    getOrgGroup: {
      providesTags: ['OrgGroup'],
    },
    getOrgGroupsList: {
      providesTags: ['OrgGroups'],
    },
    createOrgInvite: {
      invalidatesTags: createInvalidate(['OrgInvite'] as const),
    },
    deleteOrgInvite: {
      invalidatesTags: createInvalidate(['OrgInvite'] as const),
    },
    updateOrgUser: {
      invalidatesTags: createInvalidate(['OrgUser'] as const),
    },
    deleteOrgUser: {
      invalidatesTags: createInvalidate(['OrgUser', 'OrgPlan'] as const),
    },
    getOrgInvitesList: {
      providesTags: ['OrgInvite'],
    },
    getOrgUserList: {
      providesTags: ['OrgUser'],
    },
    getOrgInvoiceList: {
      transformResponse: (response: GetOrgInvoiceListApiResponse) => ({
        ...response,
        months: [...(response.months || [])].reverse(),
      }),
    },
    getOrgPaymentsCreditCards: {
      providesTags: ['OrgCreditCard'],
    },
    getOrgPlan: {
      providesTags: ['OrgPlan'],
    },
    getOrganization: {
      providesTags: ['Organization'],
    },
    getOrgBalance: {
      providesTags: ['OrgBalance'],
    },
    postOrgPaymentsCreditCards: {
      invalidatesTags: createInvalidate(['OrgCreditCard'] as const),
    },
    deleteOrgPaymentsCreditCard: {
      invalidatesTags: createInvalidate(['OrgCreditCard'] as const),
    },
    postOrgPaymentsCreditCardCharge: {
      invalidatesTags: createInvalidate(['OrgBalance'] as const),
    },
    transferOrgFunds: {
      invalidatesTags: createInvalidate(['OrgBalance'] as const),
    },
    updateOrgBalance: {
      invalidatesTags: createInvalidate(['OrgBalance'] as const),
    },
    createOrgBankTransferPdf: {
      query: (queryArg) => ({
        url: `/v1/organizations/${queryArg.orgId}/banktransfer/`,
        method: 'POST',
        body: queryArg.body,
        responseHandler: (response) => response.blob(),
      }),
    },
    getOrgSenderIds: {
      providesTags: ['OrgSenderId'],
    },
    getSnippetList: {
      providesTags: ['OrgSnippet'],
    },
    createSnippet: {
      invalidatesTags: createInvalidate(['OrgSnippet'] as const),
    },
    updateSnippet: {
      invalidatesTags: createInvalidate(['OrgSnippet'] as const),
    },
    deleteSnippet: {
      invalidatesTags: createInvalidate(['OrgSnippet'] as const),
    },
    createContact: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    listContacts: {
      providesTags: ['Contact'],
    },
    subscribeContact: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    subscribeContacts: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    subsReopen: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    unsubscribeContact: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    unsubscribeContacts: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    updateSubscription: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    updateContact: {
      invalidatesTags: createInvalidate(['Contact'] as const),
    },
    updateOrganization: {
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled

          dispatch(
            baseApi.util.updateQueryData('getOrganization', { orgId: arg.orgId }, (data) => ({
              ...data,
              ...arg.body,
            })),
          )
        } catch {
          // do nothing
        }
      },
    },
    addOrgGroupsContacts: {
      invalidatesTags: createInvalidate(['OrgGroup', 'OrgGroups'] as const),
    },
    deleteOrgGroupContacts: {
      invalidatesTags: createInvalidate(['OrgGroup', 'OrgGroups'] as const),
    },
    createOrgGroup: {
      invalidatesTags: createInvalidate(['OrgGroups'] as const),
    },
    updateOrgGroup: {
      invalidatesTags: createInvalidate(['OrgGroup', 'OrgGroups'] as const),
    },
    deleteOrgGroup: {
      invalidatesTags: createInvalidate(['OrgGroups'] as const),
    },
    updateOrgPlan: {
      invalidatesTags: createInvalidate(['OrgPlan'] as const),
    },
    cancelOrgPlan: {
      invalidatesTags: createInvalidate(['OrgPlan'] as const),
    },
    getExportsList: {
      providesTags: ['OrgExport'],
    },
    deleteExport: {
      invalidatesTags: createInvalidate(['OrgExport'] as const),
    },
    getOrgWaTemplatesList: {
      providesTags: ['WaTemplate'],
    },
    getOrgWaTemplatesGet: {
      providesTags: ['WaTemplate'],
    },
    getOrgWaTemplatesRefresh: {
      invalidatesTags: createInvalidate(['WaTemplate'] as const),
    },
    getOrgWaTemplatesRegister: {
      invalidatesTags: createInvalidate(['WaTemplate'] as const),
    },
    getCampaign: {
      providesTags: ['OrgCampaign'],
    },
    listCampaigns: {
      providesTags: ['OrgCampaign'],
    },
    createCampaign: {
      invalidatesTags: createInvalidate(['OrgCampaign', 'OrgQuota'] as const),
    },
    updateCampaign: {
      invalidatesTags: createInvalidate(['OrgCampaign', 'OrgQuota'] as const),
    },
    deleteCampaign: {
      invalidatesTags: createInvalidate(['OrgCampaign', 'OrgQuota'] as const),
    },
    abortCampaign: {
      invalidatesTags: createInvalidate(['OrgCampaign'] as const),
    },
    setCampaignOpen: {
      invalidatesTags: createInvalidate(['OrgCampaign'] as const),
    },
    copyCampaign: {
      invalidatesTags: createInvalidate(['OrgCampaign', 'OrgQuota'] as const),
    },
    setCampaignArchived: {
      invalidatesTags: createInvalidate(['OrgCampaign'] as const),
    },
    setCampaignPublished: {
      invalidatesTags: createInvalidate(['OrgCampaign'] as const),
    },
    listOrgQuotas: {
      providesTags: ['OrgQuota'],
    },
    createOrgQuota: {
      invalidatesTags: createInvalidate(['OrgQuota'] as const),
    },
    updateOrgQuota: {
      invalidatesTags: createInvalidate(['OrgQuota'] as const),
    },
    deleteOrgQuota: {
      invalidatesTags: createInvalidate(['OrgCampaign', 'OrgQuota'] as const),
    },
    getUserRecoveryCodes: {
      providesTags: ['TwoFactor'],
    },
    newUserRecoveryCodes: {
      invalidatesTags: createInvalidate(['TwoFactor'] as const),
    },
    disableUserTotp: {
      invalidatesTags: createInvalidate(['TwoFactor'] as const),
    },
    listUserAuthTokens: {
      providesTags: ['AuthToken'],
    },
    getUserAuthToken: {
      providesTags: ['AuthToken'],
    },
    createUserAuthToken: {
      invalidatesTags: createInvalidate(['AuthToken'] as const),
    },
    updateUserAuthToken: {
      invalidatesTags: createInvalidate(['AuthToken'] as const),
    },
    deleteUserAuthToken: {
      invalidatesTags: createInvalidate(['AuthToken'] as const),
    },
    uploadFile: {
      invalidatesTags: createInvalidate(['OrgFiles'] as const),
      query: (args: UploadFileApiArg) => {
        const formData = new FormData()
        args.body.upload.forEach((file) => {
          formData.append('upload', file)
        })
        return {
          url: `/v1/organizations/${args.orgId}/files/`,
          method: 'POST',
          body: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: {
            category: args.category,
            filter: args.filter,
            video: args.video,
          },
        }
      },
    },
  },
})

export type ErrorResponse = {
  status: number
  data: {
    error: string
    errorCode?: string // can be undefined for older errors
    errorContext?: Record<string, any>
  }
}

export const isErrorResponse = (error: unknown): error is ErrorResponse => {
  return (
    !!error &&
    typeof error === 'object' &&
    'status' in error &&
    'data' in error &&
    typeof error.data === 'object' && // 404 error from backend is a string so we need to handle that
    'error' in (error.data as any)
  )
}

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (!isErrorResponse(error)) {
    return fallback
  }

  const { error: errorMessage, errorCode } = error.data
  return (errorCode && errorCodes[errorCode]) || errorMessage || fallback
}
